/**
 * @fileoverview Client-side store with optimistic updates.
 *
 * Store manages three layers per table, with a single global version:
 *   1. serverState — confirmed rows from the server, keyed by table then primary key
 *   2. inflight — batch of intents currently being sent to the server (multi-table)
 *   3. pending — mutation intents queued to send in the next batch (any table)
 *   4. merged(table) — combines all layers into a single view for the UI
 *
 * Mutations are serialized: only one batch is in flight at a time.
 * Each batch drains ALL pending intents across all tables into a single
 * multi-table batch. Intents accumulate in pending while a batch is in
 * flight, then all are drained and sent when the current batch completes.
 *
 * @module Store
 */
import type { StoreSchema } from "../models/StoreSchema";
import type { ChangeEntry, RawOperation, Row, Transport } from "./Transport";

/**
 * A high-level mutation intent.
 * Stored in the pending queue and materialized into a RawOperation at send time.
 */
export type MutationIntent =
	| { type: "INSERT"; value: Row }
	| { type: "UPDATE"; key: string | number; patch: Partial<Row> }
	| { type: "DELETE"; key: string | number }
	| { type: "UPSERT"; value: Row }
	| { type: "CREATE_TABLE"; primaryKey: string }
	| { type: "DROP_TABLE" };

/** Subscriber callback type. */
type Listener = () => void;

/**
 * Extracts the primary key value from a row.
 */
function getKey(row: Row, pk: string): string | number {
	return row[pk] as string | number;
}

/**
 * Client-side view of server tables with optimistic mutation support.
 *
 * Mutations are serialized: they enqueue an intent for instant UI feedback,
 * then are batched and sent as a single RPC. Only one batch is in flight
 * at a time. The server handles ordering and conflict detection.
 * Use allOpsSettled() to await all in-flight and pending mutations.
 *
 * @example
 * ```ts
 * const store = new Store({ users: { primaryKey: 'id' } }, transport)
 * await store.hydrate('users')
 * store.subscribe(() => renderUI(store.merged('users')))
 * store.upsert('users', { id: 1, name: 'Alice' })
 * await store.allOpsSettled()
 * ```
 */
export class Store {
	private readonly serverState = new Map<string, Map<string | number, Row>>();
	private pending: Array<{ table: string; intent: MutationIntent }> = [];
	private inflight: Array<{ table: string; intent: MutationIntent; op: RawOperation }> = [];
	private inflightPromise: Promise<void> | null = null;
	private readonly listeners = new Set<Listener>();
	private serverVersion = 0;
	private hydrateGeneration = 0;
	private pullPromise: Promise<void> | null = null;

	constructor(
		private readonly schema: StoreSchema,
		private readonly transport: Transport
	) {}

	get version(): number {
		return this.serverVersion;
	}

	get pendingCount(): number {
		return this.pending.length + this.inflight.length;
	}

	// ── Mutations ───────────────────────────────────────────

	createTable(table: string, primaryKey = "id"): void {
		if (this.schema[table]) return;
		this.schema[table] = { primaryKey };
		this.enqueue(table, { type: "CREATE_TABLE", primaryKey });
	}

	dropTable(table: string): void {
		delete this.schema[table];
		this.serverState.delete(table);
		this.pending = this.pending.filter((e) => e.table !== table);
		this.inflight = this.inflight.filter((e) => e.table !== table);
		this.enqueue(table, { type: "DROP_TABLE" });
	}

	insert(table: string, value: Row): void {
		this.enqueue(table, { type: "INSERT", value });
	}

	update(table: string, key: string | number, patch: Partial<Row>): void {
		this.enqueue(table, { type: "UPDATE", key, patch });
	}

	delete(table: string, key: string | number): void {
		this.enqueue(table, { type: "DELETE", key });
	}

	upsert(table: string, value: Row): void {
		this.enqueue(table, { type: "UPSERT", value });
	}

	// ── Reads ───────────────────────────────────────────────

	/**
	 * Returns the list of table names known to the store.
	 */
	tableNames(): string[] {
		return Object.keys(this.schema);
	}

	/**
	 * Returns the schema definition for a table, or undefined if the table doesn't exist.
	 */
	tableSchema(table: string): { primaryKey: string } | undefined {
		return this.schema[table];
	}

	/**
	 * Computes a merged view of server state + inflight + pending intents for a table.
	 * Replays inflight intents over a copy of serverState, then pending.
	 */
	merged(table: string): Row[] {
		const pk = this.schema[table]!.primaryKey;
		const state = new Map(this.getTableState(table));
		for (const entry of this.inflight) {
			if (entry.table === table) Store.applyIntent(state, entry.intent, pk);
		}
		for (const entry of this.pending) {
			if (entry.table === table) Store.applyIntent(state, entry.intent, pk);
		}
		return Array.from(state.values());
	}

	// ── Sync ────────────────────────────────────────────────

	/**
	 * Fetches full server state for all tables. Used for initial load.
	 * Provides a consistent cross-table snapshot in a single call.
	 */
	async hydrate(): Promise<void> {
		const gen = ++this.hydrateGeneration;
		const { version, tables } = await this.transport.state();
		if (gen !== this.hydrateGeneration) return;
		this.hydrateFrom(version, tables);
	}

	/**
	 * Hydrates the store from pre-fetched snapshot data.
	 * Used when the caller already has version + tables (e.g. from EnsureCreatedResponse).
	 */
	hydrateFrom(version: number, tables: Record<string, Row[]>): void {
		if (version < this.serverVersion) return;
		for (const [table, rows] of Object.entries(tables)) {
			const tableDef = this.schema[table];
			if (!tableDef) continue;
			const tableState = this.getOrCreateTableState(table);
			tableState.clear();
			for (const row of rows) tableState.set(getKey(row, tableDef.primaryKey), row);
		}
		this.serverVersion = version;
		this.notify();
	}

	/**
	 * Fetches incremental changes since the last known version.
	 */
	async pull(): Promise<void> {
		const { version, changes } = await this.transport.changes(this.serverVersion);
		this.receiveChanges(version, changes);
	}

	/**
	 * Applies confirmed server changes to local state.
	 * Drops stale versions. Skips entries already seen.
	 * If there is a gap (missing versions), triggers a pull() to fill it.
	 */
	receiveChanges(version: number, changes: ChangeEntry[]): void {
		if (version <= this.serverVersion) return;

		const firstNew = changes.find((e) => e.version > this.serverVersion);
		if (!firstNew || firstNew.version > this.serverVersion + 1) {
			this.schedulePull();
			return;
		}

		for (const entry of changes) {
			if (entry.version <= this.serverVersion) continue;
			const op = entry.operation;

			// Schema operations
			if (op.type === "CREATE_TABLE") {
				this.createTable(op.table, op.primaryKey);
				continue;
			}
			if (op.type === "DROP_TABLE") {
				delete this.schema[op.table];
				this.serverState.delete(op.table);
				this.pending = this.pending.filter((e) => e.table !== op.table);
				this.inflight = this.inflight.filter((e) => e.table !== op.table);
				continue;
			}

			// Data operations
			const table = op.table;
			const tableDef = this.schema[table];
			if (!tableDef) continue;
			const tableState = this.getOrCreateTableState(table);
			Store.applyOp(tableState, op, tableDef.primaryKey);
		}
		this.serverVersion = version;
		this.notify();
	}

	/**
	 * Awaits all in-flight mutations, pending mutations, and any recovery pull.
	 */
	async allOpsSettled(): Promise<void> {
		while (this.inflightPromise || this.pending.length > 0 || this.pullPromise) {
			if (this.pullPromise) await this.pullPromise;
			if (this.inflightPromise) await this.inflightPromise;
		}
	}

	// ── Subscribe ───────────────────────────────────────────

	subscribe(cb: Listener): () => void {
		this.listeners.add(cb);
		return () => {
			this.listeners.delete(cb);
		};
	}

	// ── Internals ───────────────────────────────────────────

	private getTableState(table: string): Map<string | number, Row> {
		return this.serverState.get(table) ?? new Map();
	}

	private getOrCreateTableState(table: string): Map<string | number, Row> {
		let state = this.serverState.get(table);
		if (!state) {
			state = new Map();
			this.serverState.set(table, state);
		}
		return state;
	}

	private schedulePull(): void {
		if (this.pullPromise) return;
		this.pullPromise = this.pull()
			.catch(() => {})
			.finally(() => {
				this.pullPromise = null;
			});
	}

	private enqueue(table: string, intent: MutationIntent): void {
		this.pending.push({ table, intent });
		this.notify();
		this.trySend();
	}

	/**
	 * If no batch is in flight and pending is non-empty, drains ALL pending
	 * intents across all tables, validates each sequentially against per-table
	 * simulated state, and sends the valid ones as a single multi-table batch.
	 */
	private trySend(): void {
		if (this.inflightPromise) return;
		if (this.pending.length === 0) return;

		// Reset pending.
		const pendingMutations = this.pending;
		this.pending = [];

		// Memoized table snapshots.
		const tableSnapshots = new Map<string, Map<string | number, Row>>();
		const tableSnapshot = (table: string): Map<string | number, Row> => {
			let snap = tableSnapshots.get(table);
			if (!snap) {
				snap = new Map(this.getTableState(table));
				tableSnapshots.set(table, snap);
			}
			return snap;
		};

		// Accumulate sendable mutations.
		const sendable: Array<{ table: string; intent: MutationIntent; op: RawOperation }> = [];
		for (const { table, intent } of pendingMutations) {
			if (intent.type === "CREATE_TABLE") {
				sendable.push({
					table,
					intent,
					op: { type: "CREATE_TABLE", table, primaryKey: intent.primaryKey },
				});
				continue;
			}
			if (intent.type === "DROP_TABLE") {
				sendable.push({ table, intent, op: { type: "DROP_TABLE", table } });
				continue;
			}
			const tableDef = this.schema[table];
			if (!tableDef) continue;
			const pk = tableDef.primaryKey;
			const snapshot = tableSnapshot(table);
			const lookup = (key: string | number) => snapshot.get(key);
			const op = Store.validateIntentWith(table, intent, lookup, pk);
			if (!op) continue;
			sendable.push({ table, intent: intent, op });
			Store.applyIntent(snapshot, intent, pk);
		}

		if (sendable.length === 0) {
			this.notify();
			return;
		}

		// Send the batch of mutations.
		this.inflight = sendable;
		const ops: RawOperation[] = sendable.map((b) => b.op);

		this.inflightPromise = this.transport
			.mutate(this.serverVersion, ops)
			.then(({ version, changes }) => {
				this.receiveChanges(version, changes);
				this.inflight = [];
				this.inflightPromise = null;
				this.notify();
				this.trySend();
			})
			.catch((error: unknown) => {
				this.inflight = [];
				this.inflightPromise = null;
				this.notify();
				this.trySend();

				if (error instanceof TypeError || error instanceof RangeError) {
					throw error;
				}
			});
	}

	private static validateIntentWith(
		table: string,
		intent: MutationIntent,
		lookup: (key: string | number) => Row | undefined,
		pk: string
	): RawOperation | undefined {
		switch (intent.type) {
			case "INSERT": {
				const key = getKey(intent.value, pk);
				if (lookup(key)) return undefined;
				return { type: "INSERT", table, value: intent.value };
			}
			case "DELETE": {
				const cur = lookup(intent.key);
				if (!cur) return undefined;
				return { type: "DELETE", table, value: cur };
			}
			case "UPDATE": {
				const cur = lookup(intent.key);
				if (!cur) return undefined;
				return {
					type: "REPLACE",
					table,
					value: { ...cur, ...intent.patch },
				};
			}
			case "UPSERT": {
				return {
					type: "UPSERT",
					table,
					value: intent.value,
				};
			}
		}
	}

	private static applyIntent(
		state: Map<string | number, Row>,
		intent: MutationIntent,
		pk: string
	): void {
		switch (intent.type) {
			case "INSERT":
				state.set(getKey(intent.value, pk), intent.value);
				break;
			case "DELETE":
				state.delete(intent.key);
				break;
			case "UPDATE": {
				const cur = state.get(intent.key);
				if (cur) state.set(intent.key, { ...cur, ...intent.patch });
				break;
			}
			case "UPSERT":
				state.set(getKey(intent.value, pk), intent.value);
				break;
			case "CREATE_TABLE":
			case "DROP_TABLE":
				break;
		}
	}

	private static applyOp(
		tableState: Map<string | number, Row>,
		op: RawOperation,
		pk: string
	): void {
		switch (op.type) {
			case "INSERT":
				tableState.set(getKey(op.value, pk), op.value);
				break;
			case "DELETE":
				tableState.delete(getKey(op.value, pk));
				break;
			case "REPLACE":
				tableState.set(getKey(op.value, pk), op.value);
				break;
			case "UPSERT":
				tableState.set(getKey(op.value, pk), op.value);
				break;
			case "CREATE_TABLE":
				// Schema change handled by receiveChanges
				break;
			case "DROP_TABLE":
				// Schema change handled by receiveChanges
				break;
		}
	}

	private notify(): void {
		for (const cb of this.listeners) cb();
	}
}
